import { useState, useCallback, useEffect } from "react";
import CustomerService from "@/services/CustomerService";

/**
 * useCustomerData Hook - More comprehensive version with filtering support
 * Shows how the service can handle more complex scenarios
 */
export default function useCustomerData() {
  // Data State
  const [customerData, setCustomerData] = useState([]);
  const [stats, setStats] = useState({});

  // UI State
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);

  // Error/Message State
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  // Pagination State
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  // Filter/Sort State
  const [filters, setFilters] = useState({});
  const [sortColumn, setSortColumn] = useState('created');
  const [sortDirection, setSortDirection] = useState('desc');
  const [limit, setLimit] = useState(50);

  /**
   * Load customers with current filters and pagination
   */
  const loadCustomerData = useCallback(async () => {
    // console.log(`Loading customer data, limit: ${limit} filters: `, filters);
    setLoadingMore(prevLoadingMore => {
      if (!prevLoadingMore) setLoadingCustomers(true);
      return prevLoadingMore;
    });
    setError(null);

    const result = await CustomerService.getCustomersWithFilters({
      limit,
      sortColumn,
      sortDirection,
      filters
    });

    // console.log('Customer data result:', result);

    if (result.error) {
      setError(result.error);
      setCustomerData([]);
      setTotalCount(0);
      setHasMore(false);
    } else {
      setCustomerData(result.data);
      setTotalCount(result.totalCount);
      setHasMore(result.hasMore);
    }

    setLoadingCustomers(false);
    setLoadingMore(false);
  }, [limit, sortColumn, sortDirection, filters]);

  /**
   * Load customer statistics
   */
  const loadStats = useCallback(async () => {
    setLoadingStats(true);
    setError(null);

    const result = await CustomerService.getCustomerStats();

    if (result.error) {
      setError(result.error);
      setStats({});
    } else {
      setStats(result.data);
    }

    setLoadingStats(false);
  }, []);

  /**
   * Load more customers (pagination)
   */
  const handleLoadMore = useCallback(() => {
    if (hasMore && !loadingMore) {
      setLimit(prevLimit => prevLimit + 50);
      setLoadingMore(true);
    }
  }, [hasMore, loadingMore]);

  /**
   * Update sorting
   */
  const setSorting = useCallback((column, direction) => {
    setSortColumn(column);
    setSortDirection(direction);
  }, []);

  /**
   * Update filters
   */
  const updateFilters = useCallback((newFilters) => {
    setFilters(prevFilters => ({ ...prevFilters, ...newFilters }));
  }, []);

  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    setFilters({});
    // setLimit(50);
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Clear message state
   */
  const clearMessage = useCallback(() => {
    setMessage(null);
  }, []);

  return {
    // Data
    customerData,
    stats,
    totalCount,
    hasMore,

    // UI States
    loadingCustomers,
    loadingMore,
    loadingStats,
    error,
    message,

    // Filter/Sort State
    filters,
    sortColumn,
    sortDirection,
    limit,

    // Actions
    loadCustomerData,
    loadStats,
    handleLoadMore,
    setCustomerSorting: setSorting,
    updateFilters,
    clearFilters,

    // State management
    setFilters,
    setError: clearError,
    setMessage: clearMessage
  };
}
